import React, { createRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Button, PaperIcon, Popover } from '@nackle/paper';
import { UserOutlined } from '@ant-design/icons';
import * as microsoftTeams from '@microsoft/teams-js';
import { LocaleString, getString } from '@nackle/intl-tools';
import { USER_STORAGE_KEY } from '../../app-auth';
import { ArrowLeft, ArrowRight } from '../shared/icons/icons';
import Program from './program/Program';
import People from './people/People';
import Message from './message/Message';
import Review from './review/Review';
import Success from './success/Success.jsx';
import BudgetMeter from '../shared/budget-meter/BudgetMeter';
import envConstants from 'env-constants';
import { profileUrl } from 'endpoints';
import { clearRecognition, submitRecognition } from '../../app-dux/recognition/actions';
import { removeSelectedPerson } from './people/actions';
import { getSelectedPeople } from './people/selectors';
import { getRemainingBudget, getRemainingBudgetPercent, getTotalAward } from './awards/selectors';
import { getSelectedBehavior, getSelectedBehaviorId, getSelectedProgram } from './program/selectors';
import { getMessage, getSelectedEcard } from './message/selectors';
import { clearErrors, getErrors } from '../../app-dux/app-dux';

microsoftTeams.initialize();

export default function Recognize( props ) {
    const { company,
        integrationType,
        loading,
        user } = props;
    const dispatch = useDispatch();
    const selectedPeople = useSelector( ( state ) => getSelectedPeople( state ) );
    const remainingBudget = useSelector( ( state ) => getRemainingBudget( state ) );
    const remainingBudgetPercent = useSelector( ( state ) => getRemainingBudgetPercent( state ) );
    const totalAward = useSelector( ( state ) => getTotalAward( state ) );
    const selectedProgram = useSelector( ( state ) => getSelectedProgram( state ) );
    const message = useSelector( ( state ) => getMessage( state ) );
    const selectedEcard = useSelector( ( state ) => getSelectedEcard( state ) );
    const selectedBehavior = useSelector( ( state ) => getSelectedBehavior( state ) );
    const selectedBehaviorId = useSelector( ( state ) => getSelectedBehaviorId( state ) );
    const errors = useSelector( ( state ) => getErrors( state ) );
    const [ activeStep, setActiveStep ] = useState( 'people' );
    const [ forward, setForward ] = useState( true );
    const [ personPopoverVisible, setPersonPopoverVisible ] = useState( false );
    const [ awardPopoverVisible, setAwardPopoverVisible ] = useState( false );

    const ineligibleRecipients = [];
    errors.map( error => {
        if ( error.personId && error.field !== 'senderPersonId' && error.selectionType === 'MESSAGE' ) {
            ineligibleRecipients.push( error.personId );
        }
    } );

    const peoplePopupRef = createRef();
    const awardPopupRef = createRef();

    const handlePeopleEdit = () => {
        setActiveStep( 'people' );
        setPersonPopoverVisible( false );
    };

    const handlePeopleRemove = async ( person ) => {
        await dispatch( removeSelectedPerson( person ) );
        if ( selectedPeople.length === 0 ) {
            dispatch( clearErrors() );
            dispatch( clearRecognition() );
            setActiveStep( 'people' );
            setPersonPopoverVisible( false );
        }
    };

    const handleAwardEdit = () => {
        setActiveStep( 'program' );
        setAwardPopoverVisible( false );
    };

    const disableButton = () => {
        const { isBehaviorRequired, pointsRequired, ecardRequired } = selectedProgram;
        let awardsComplete = true;
        selectedPeople.map( ( person ) => {
            if ( pointsRequired && ( !person.awardValue || person.awardValue === 0 ) ) {
                awardsComplete = false;
                return;
            }
        } );
        return errors.length > 0 ||
            activeStep === 'program' &&
            ( Object.keys( selectedProgram ).length === 0 ||
                ( isBehaviorRequired && !selectedBehaviorId ) ||
                !awardsComplete ||
                remainingBudget < 0 ) ||
            activeStep === 'people' && selectedPeople.length === 0 ||
            activeStep === 'message' &&
            ( !message ||
                ( ecardRequired && Object.keys( selectedEcard ).length === 0 ) );
    };

    const getForwardButton = () => {
        switch ( activeStep ) {
            case 'people':
                return getString( 'recognition.button.saveRecipients' );
            case 'program':
                return getString( 'recognition.button.saveProgram' );
            case 'message':
                return getString( 'recognition.button.review' );
            case 'review':
                return getString( 'recognition.button.send' );
            case 'success':
                return getString( 'recognition.button.done' );
            default:
                return getString( 'recognition.button.saveRecipients' );
        }
    };

    const stepForward = async () => {
        setForward( true );
        switch ( activeStep ) {
            case 'people':
                setActiveStep( 'program' );
                break;
            case 'program':
                setActiveStep( 'message' );
                break;
            case 'message':
                setActiveStep( 'review' );
                break;
            case 'review':
                const results = await dispatch( submitRecognition( integrationType, 'SUBMITTED' ) );
                if ( results.success ) {
                    setActiveStep( 'success' );
                }
                if ( integrationType === 'TEAMS' ) {
                    microsoftTeams.tasks.submitTask( {
                        submitType: 'recognition',
                        recipients: selectedPeople,
                        ecard: selectedEcard,
                        message: message,
                        recognitionUrl: 'https://bravo.inspireandperform.com/bravo/login.do',
                        user: JSON.parse( localStorage.getItem( USER_STORAGE_KEY ) )
                    },
                        envConstants.MICROSOFT_APP_ID );

                }
                break;
            case 'success':
                dispatch( clearRecognition() );
                setActiveStep( 'people' );
                break;
            default:
                setActiveStep( 'program' );
                break;
        }
    };

    const stepBack = () => {
        setForward( false );
        switch ( activeStep ) {
            case 'program':
                setActiveStep( 'people' );
                break;
            case 'message':
                setActiveStep( 'program' );
                break;
            case 'review':
                setActiveStep( 'message' );
                break;
            case 'success':
                dispatch( clearRecognition() );
                setActiveStep( 'people' );
                break;
            default:
                setActiveStep( 'people' );
                break;
        }
    };

    return (
        <div className='main-content-container'>
            { activeStep !== 'people' && activeStep !== 'review' && activeStep !== 'success' &&
                <div className='bread-crumb-container'>
                    { activeStep !== 'people' && selectedPeople && selectedPeople.length > 0 &&
                        <div style={ { paddingLeft: '20px' } } ref={ peoplePopupRef }>
                            <Popover
                                placement='bottomRight'
                                visible={ personPopoverVisible }
                                trigger='click'
                                onVisibleChange={ ( visible ) => setPersonPopoverVisible( visible ) }
                                getPopupContainer={ () => peoplePopupRef.current }
                                content={
                                    <div>
                                        <div className='bread-crumb-header'>
                                            <div>
                                                { selectedPeople.length == 1
                                                    ? <LocaleString code='recognition.breadcrumb.totalRecipient' />
                                                    : <LocaleString code='recognition.breadcrumb.totalRecipients' replaceOptions={ { recipientCount: selectedPeople.length } } />
                                                }
                                            </div>
                                            <div style={ { marginLeft: 'auto' } }>
                                                <Button className="btn-edit" onClick={ handlePeopleEdit }>
                                                    <LocaleString code='recognition.button.edit' />
                                                </Button>
                                            </div>
                                        </div>
                                        { selectedPeople.map( person => {
                                            return (
                                                <div key={ `team-recipient-${ person.id }` } className={ ineligibleRecipients.includes( person.id ) ? 'recipient-item recipient-error' : 'recipient-item' }>
                                                    <div>
                                                        <Avatar size={ 40 } className="primary-background-avatar" src={ `${ profileUrl }/public/images/image/${ company.id }/${ person.id }/avatar` } icon={ integrationType === 'WORKPLACE' ? <UserOutlined /> : null }>{ `${ person.givenName.slice( 0, 1 ) }${ person.surname.slice( 0, 1 ) }` }</Avatar>
                                                    </div>
                                                    <div className='recipient-info'>
                                                        <div className='recipient-name'>
                                                            { `${ person.givenName } ${ person.surname }` }
                                                        </div>
                                                        { ( person.department || person.jobTitle ) &&
                                                            <div className='recipient-email'>
                                                                { `${ person.department ? person.department : '' } ${ person.jobTitle ? person.jobTitle : '' }` }
                                                            </div>
                                                        }
                                                        <div className='recipient-email'>
                                                            { person.country }
                                                        </div>
                                                        { ineligibleRecipients.includes( person.id ) &&
                                                            <div className='recipient-error'>
                                                                <LocaleString code='recognition.error.ineligible' />
                                                            </div>
                                                        }
                                                    </div>
                                                    { ineligibleRecipients.includes( person.id ) &&
                                                        <Button style={ { marginLeft: 'auto', borderColor: '#ad1b05', backgroundColor: '#ad1b05' } }
                                                            type='primary'
                                                            onClick={ () => handlePeopleRemove( person ) }>
                                                            <LocaleString code='recognition.button.remove' />
                                                        </Button>
                                                    }
                                                    { person.awardValue && person.awardValue > 0
                                                        ?
                                                        <div className='recipient-points'>
                                                            { `${ person.awardValue } ${ getString( 'mediaLabel' ) }` }
                                                        </div>
                                                        :
                                                        null
                                                    }
                                                </div>
                                            );
                                        } ) }
                                    </div>
                                }>
                                <Button className={ ineligibleRecipients.length > 0 ? 'bread-crumb-button errors' : 'bread-crumb-button' }>
                                    <div className='button-content'>
                                        <div className='button-text '>{ selectedPeople.length === 1
                                            ? <LocaleString code='recognition.breadcrumb.totalRecipient' />
                                            : <LocaleString code='recognition.breadcrumb.totalRecipients' replaceOptions={ { recipientCount: selectedPeople.length } } />
                                        }</div>
                                        <div><PaperIcon icon={ <ArrowRight className='rotate-icon' /> } /></div>
                                    </div>
                                </Button>
                            </Popover>
                        </div>
                    }
                    { activeStep !== 'program' && selectedPeople && selectedPeople.length > 0 &&
                        <div ref={ awardPopupRef }>
                            <Popover
                                placement={ integrationType === 'WORKPLACE' ? 'bottom' : 'bottomLeft' }
                                visible={ awardPopoverVisible }
                                trigger='click'
                                onVisibleChange={ ( visible ) => setAwardPopoverVisible( visible ) }
                                getPopupContainer={ () => awardPopupRef.current }
                                content={
                                    <div>
                                        <div className='bread-crumb-header'>
                                            <div style={ { paddingRight: '10px', flexWrap: 'wrap' } }>
                                                { selectedProgram.rewardChoiceName }
                                            </div>
                                            <div style={ { marginLeft: 'auto' } }>
                                                <Button className="btn-edit" onClick={ handleAwardEdit }>
                                                    <LocaleString code='recognition.button.edit' />
                                                </Button>
                                            </div>
                                        </div>
                                        { selectedBehavior &&
                                            <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', paddingBottom: '10px' } }>
                                                { selectedBehavior.imageUrl &&
                                                    <Avatar alt="" style={ { background: 'transparent' } } src={ selectedBehavior.imageUrl }></Avatar>
                                                }
                                                <div style={ { paddingLeft: '5px' } }>{ selectedBehavior.name }</div>
                                            </div>
                                        }
                                        { selectedProgram.hasPoints &&
                                            <div>
                                                <BudgetMeter
                                                    percent={ remainingBudgetPercent }
                                                    current={ remainingBudget }
                                                    headerText={ totalAward }
                                                //additionalInfo={selectedProgram.rewardChoiceName}
                                                />
                                            </div>
                                        }
                                    </div>
                                }>
                                <Button className='bread-crumb-button'>
                                    <div className='button-content'>
                                        <div className='button-text'>
                                            <LocaleString code='recognition.breadcrumb.program' />
                                        </div>
                                        <div><PaperIcon icon={ <ArrowRight className='rotate-icon' /> } /></div>
                                    </div>
                                </Button>
                            </Popover>
                        </div>
                    }
                </div>
            }
            <div className="content-container">
                { activeStep === 'people' &&
                    <People forward={ forward } company={ company } integrationType={ integrationType } />
                }
                { activeStep === 'program' &&
                    <Program forward={ forward }
                        company={ company }
                        integrationType={ integrationType }
                        ineligibleRecipients={ ineligibleRecipients } />
                }
                { activeStep === 'message' &&
                    <Message forward={ forward }
                        user={ user } />
                }
                { activeStep === 'review' &&
                    <Review company={ company }
                        forward={ forward }
                        editPage={ ( step ) => setActiveStep( step ) }
                        integrationType={ integrationType }
                        loading={ loading }
                        user={ user } />
                }
                { activeStep === 'success' &&
                    <Success forward={ forward } />
                }
            </div>
            { ( integrationType === 'TEAMS' && activeStep === 'success' ) ? null :
                <div className="button-container">
                    { activeStep !== 'people' &&
                        <Button className={ activeStep !== 'success' ? 'btn back-button' : 'btn' }
                            onClick={ stepBack }
                            icon={ activeStep !== 'success' ? <PaperIcon icon={ <ArrowLeft className='back-button-icon' fill={ integrationType === 'WORKPLACE' ? '#000' : '#6266a5' } /> } /> : null } >
                            { activeStep === 'success' ? getString( 'recognition.button.sendAnother' ) : getString( 'recognition.button.back' ) }
                        </Button>
                    }
                    { ( activeStep === 'success' && integrationType === 'WORKPLACE' ) ?
                        null
                        :
                        <Button className='next-button'
                            loading={ loading && activeStep === 'review' }
                            disabled={ disableButton() }
                            onClick={ ( integrationType === 'CHROME' && activeStep === 'success' ) ? () => { parent.window.close(); } : stepForward }>
                            { getForwardButton() }
                        </Button>
                    }
                </div>
            }
        </div>
    );
}